import { applyTargetSelection, pruneOrphanedBioscopeMovements } from './stateHelpers';
import type { OperateWizardAction, OperateWizardState } from './types';

export type MergeTargetAction = Extract<
    OperateWizardAction,
    { type: 'setOccupiedTarget' | 'setEmptyTarget' | 'setSecondSource' | 'clearTarget' }
>;

/** The split-and-merge target actions, which keep sources and destinations in step. */
export function reduceMergeTargetAction(
    state: OperateWizardState,
    action: MergeTargetAction
): OperateWizardState {
    switch (action.type) {
        case 'setOccupiedTarget': {
            // Split-and-merge bookkeeping (occupied branch). The continuation
            // lives at destinations[0] (created earlier in the flow); the
            // merge target lands at destinations[1] with both inflows
            // pre-wired. The occupant becomes the second source so the
            // backend closes it.
            //
            // Replays cleanly when the operator switches the target between
            // cages: any previously-added second source (occupant or empty-
            // branch absorbee) is removed before the new occupant is added.
            return applyTargetSelection(state, {
                mode: 'occupied',
                cageId: action.cageId,
                secondSource: {
                    fishGroupId: action.occupantFishGroupId,
                    name: action.occupantName,
                    currentCageId: action.occupantCageId,
                },
            });
        }

        case 'setEmptyTarget': {
            // Split-and-merge bookkeeping (empty branch). The merge target
            // lands at destinations[1] with a single inflow (initial source).
            // The operator will pick the second source explicitly in the next
            // step; until then `sources` stays at `[initial]`.
            return applyTargetSelection(state, {
                mode: 'empty',
                cageId: action.cageId,
                secondSource: null,
            });
        }

        case 'setSecondSource': {
            // Split-and-merge bookkeeping (empty branch, continued). Adds the
            // operator-chosen FG as the second source and wires its inflow
            // into destinations[1].receivesFrom. Replaces any previous
            // second source so re-picking is non-destructive.
            const target = state.destinations[1];
            if (!target || state.samTargetMode !== 'empty') return state;
            const sourcesWithoutSecond = state.sources.filter(
                (s) => s.fishGroupId === state.initialSourceId
            );
            const sources = [
                ...sourcesWithoutSecond,
                {
                    fishGroupId: action.fishGroupId,
                    name: action.name,
                    currentCageId: action.currentCageId,
                },
            ];
            // Reset the merge target's estimated count when the second
            // source changes — any earlier prefill was tied to a different
            // FG's population. The Name and Notes the operator typed in the
            // target step survive.
            const isSameSource =
                target.receivesFrom.find((i) => i.sourceFishGroupId !== state.initialSourceId)
                    ?.sourceFishGroupId === action.fishGroupId;
            const destinations = state.destinations.map((d, idx) =>
                idx === 1
                    ? {
                          ...d,
                          estimatedFishCount: isSameSource ? d.estimatedFishCount : '',
                          receivesFrom: [
                              {
                                  sourceFishGroupId: state.initialSourceId,
                                  estimatedFishCount: '',
                              },
                              { sourceFishGroupId: action.fishGroupId, estimatedFishCount: '' },
                          ],
                      }
                    : d
            );
            return { ...state, sources, destinations };
        }

        case 'clearTarget': {
            // Drop the merge target at destinations[1] and any second source
            // that came along with it (occupant or empty-branch absorbee).
            // Noop when there is no target — never peels off the continuation
            // at [0] by accident.
            if (state.destinations.length < 2) return state;
            const sources = state.sources.filter((s) => s.fishGroupId === state.initialSourceId);
            return pruneOrphanedBioscopeMovements({
                ...state,
                sources,
                destinations: state.destinations.slice(0, 1),
                samTargetMode: null,
            });
        }

        default: {
            const _exhaustive: never = action;
            return _exhaustive;
        }
    }
}
